import { ShoppingCart, X, Minus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useCart } from "../lib/cart-context";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";

function formatPrix(p: number | null) {
  if (p === null) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p);
}

export function FloatingCart() {
  const { items, totalItems, removeItem, updateQty, clearCart } = useCart();
  const { brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const [open, setOpen] = useState(false);
  const primary = brand?.couleurPrimaire || "#2563eb";

  if (totalItems === 0) return null;

  const totalHT = items.reduce((sum, i) => {
    return i.prixAffiche !== null ? sum + i.prixAffiche * i.qty : sum;
  }, 0);

  return (
    <>
      {/* Bouton flottant */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 pl-4 pr-5 py-3 rounded-full text-white shadow-xl text-sm font-medium transition-transform hover:scale-105"
          style={{ backgroundColor: primary }}
          data-testid="button-floating-cart"
        >
          <div className="relative">
            <ShoppingCart className="w-5 h-5" />
            <span className="absolute -top-2 -right-2 w-4 h-4 flex items-center justify-center rounded-full bg-white text-[9px] font-bold" style={{ color: primary }}>
              {totalItems > 9 ? "9+" : totalItems}
            </span>
          </div>
          <span>Mon devis</span>
        </button>
      )}

      {/* Panneau devis */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={() => setOpen(false)} />
          <div className="relative z-10 w-full max-w-md h-full bg-card border-l border-border shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold text-foreground">Mon devis en cours</h2>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{totalItems} article{totalItems > 1 ? "s" : ""}</span>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Liste articles */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.map(item => (
                <div key={item.productId} className="flex items-start gap-3 bg-muted/40 rounded-xl p-3 border border-border">
                  {/* Miniature */}
                  <div className="w-12 h-12 rounded-lg bg-background border border-border flex-shrink-0 overflow-hidden">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-tight">{item.nomFr}</p>
                    <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
                    <p className="text-sm font-semibold text-foreground mt-1">{formatPrix(item.prixAffiche)}</p>
                  </div>

                  <div className="flex flex-col items-end gap-1.5">
                    <button
                      onClick={() => removeItem(item.productId)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      data-testid={`button-remove-item-${item.productId}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => updateQty(item.productId, item.qty - 1)}
                        className="w-5 h-5 rounded flex items-center justify-center bg-background border border-border hover:bg-secondary/60 text-foreground"
                        data-testid={`button-qty-minus-${item.productId}`}
                      >
                        <Minus className="w-2.5 h-2.5" />
                      </button>
                      <span className="text-sm font-medium text-foreground w-5 text-center">{item.qty}</span>
                      <button
                        onClick={() => updateQty(item.productId, item.qty + 1)}
                        className="w-5 h-5 rounded flex items-center justify-center bg-background border border-border hover:bg-secondary/60 text-foreground"
                        data-testid={`button-qty-plus-${item.productId}`}
                      >
                        <Plus className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total estimé HT</span>
                <span className="text-base font-bold text-foreground">{formatPrix(totalHT)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Tarifs indicatifs — un devis officiel sera émis par votre contact Jobotech.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { clearCart(); setOpen(false); }}
                  className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors"
                  data-testid="button-clear-cart"
                >
                  Vider
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/devis/nouveau"); }}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: primary }}
                  data-testid="button-go-to-devis"
                >
                  Finaliser le devis
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
